/**
 * features/finance/Attachments.tsx
 * ------------------------------------------------------------------
 * THE EVIDENCE PANEL, on any finance record that can carry it.
 *
 * A voucher says the school paid P4,500. The supplier's invoice and the
 * delivery note are what say it was true, and "produce the supporting
 * documentation" is the first thing an auditor asks for.
 *
 * CAMERA FIRST ON A PHONE. The commonest attachment in a school is a
 * photograph of a piece of paper somebody is holding, and the moment to
 * take it is at the counter — not after carrying the slip back to a
 * desk, where it stops being attached to anything. `capture` opens the
 * rear camera directly; on a desktop the same control is an ordinary
 * file picker.
 *
 * LOADS ON DEMAND. The list is fetched when the panel is opened, not
 * with the parent screen: a list of two hundred purchase orders must not
 * fire two hundred reads to draw a paperclip.
 *
 * REMOVING SAYS WHY, AND ONLY A MANAGER CAN. The record survives —
 * "removed by X because it belonged to another supplier" is a trail; a
 * file that quietly vanishes from the evidence behind a payment is not.
 * ------------------------------------------------------------------ */
import { useEffect, useRef, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  Paperclip, Camera, Upload, FileText, Image as ImageIcon, Loader2,
  ShieldAlert, Trash2, ExternalLink,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import {
  ATTACHMENT_COLLECTION, EVIDENCE_LABEL, attachmentProblem, isLive,
  type AttachmentKind, type EvidenceType, type FinanceAttachment,
} from '@/lib/attachments';
import { uploadAttachment, supersedeAttachment } from '@/features/finance/attachmentWrite';
import { glassFieldClass, btnGhost } from '@/features/finance/ui';

const size = (n: number) =>
  n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.round(n / 1024)}KB`;

export default function Attachments({
  kind, recordId, recordRef, canRemove = false, compact = false,
}: {
  kind: AttachmentKind;
  recordId: string;
  recordRef?: string;
  /** Manager-only. The storage rule enforces it too. */
  canRemove?: boolean;
  compact?: boolean;
}) {
  const { userData } = useAuth();
  const [rows, setRows] = useState<FinanceAttachment[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [evidence, setEvidence] = useState<EvidenceType>('supplier_invoice');
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  async function load() {
    try {
      const snap = await getDocs(query(
        collection(db, ATTACHMENT_COLLECTION),
        where('kind', '==', kind),
        where('recordId', '==', recordId),
      ));
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as FinanceAttachment));
    } catch (e) {
      setErr((e as Error).message);
      setRows([]);
    }
  }

  useEffect(() => { void load(); }, [kind, recordId]);

  async function onPick(file: File | undefined) {
    if (!file) return;
    const problem = attachmentProblem(file);
    if (problem) { setErr(problem); return; }

    setBusy(true);
    setErr(null);
    try {
      await uploadAttachment({
        kind, recordId, recordRef, evidence, file,
        actor: { uid: userData?.uid ?? '', name: userData?.displayName },
      });
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';
  }

  async function remove(a: FinanceAttachment) {
    const why = window.prompt(
      `Remove "${a.fileName}"?\n\n`
      + 'The file goes; the record stays and will show that you removed it. '
      + 'Why is it being removed?',
    );
    if (!why?.trim()) return;

    setBusy(true);
    try {
      await supersedeAttachment(a, why, {
        uid: userData?.uid ?? '', name: userData?.displayName,
      });
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
    setBusy(false);
  }

  const live = (rows ?? []).filter(isLive)
    .sort((a, b) => (a.uploadedAt ?? '').localeCompare(b.uploadedAt ?? ''));
  const removed = (rows ?? []).filter((a) => !isLive(a));

  return (
    <div className={compact ? '' : 'rounded-lg border border-gray-100 dark:border-gray-700/70 p-3'}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-[11px] uppercase tracking-wide text-gray-400 flex items-center gap-1">
          <Paperclip size={12} /> Evidence
          {rows && <span className="text-gray-300">· {live.length}</span>}
        </span>

        <select value={evidence} onChange={(e) => setEvidence(e.target.value as EvidenceType)}
          className={glassFieldClass('max-w-[190px] text-xs py-1')}>
          {(Object.keys(EVIDENCE_LABEL) as EvidenceType[]).map((k) => (
            <option key={k} value={k}>{EVIDENCE_LABEL[k]}</option>
          ))}
        </select>

        {/* On a phone this opens the rear camera; on a desktop it is an
            ordinary picker, so one control serves both. */}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment"
          className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
        <button type="button" className={btnGhost} disabled={busy}
          onClick={() => cameraRef.current?.click()}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
          Photograph
        </button>

        <input ref={fileRef} type="file" accept="application/pdf,image/*"
          className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
        <button type="button" className={btnGhost} disabled={busy}
          onClick={() => fileRef.current?.click()}>
          <Upload size={13} /> Attach a file
        </button>
      </div>

      {err && (
        <p className="text-[12px] text-red-600 mb-2 flex items-start gap-1">
          <ShieldAlert size={12} className="mt-0.5 shrink-0" />{err}
        </p>
      )}

      {rows === null ? (
        <p className="text-[12px] text-gray-400">Loading…</p>
      ) : live.length === 0 ? (
        <p className="text-[12px] text-gray-500">
          Nothing attached. A payment with no supporting document is an assertion.
        </p>
      ) : (
        <div className="space-y-1">
          {live.map((a) => (
            <div key={a.id} className="flex items-center gap-2 text-[13px]">
              {a.contentType === 'application/pdf'
                ? <FileText size={14} className="text-gray-400 shrink-0" />
                : <ImageIcon size={14} className="text-gray-400 shrink-0" />}
              <a href={a.downloadUrl} target="_blank" rel="noopener noreferrer"
                className="text-primaryLight hover:underline truncate flex-1">
                {a.fileName}
                <ExternalLink size={10} className="inline ml-1 -mt-0.5" />
              </a>
              <span className="text-[11px] text-gray-400 shrink-0">
                {EVIDENCE_LABEL[a.evidence]} · {size(a.sizeBytes)}
                {a.uploadedByName ? ` · ${a.uploadedByName}` : ''}
              </span>
              {canRemove && (
                <button type="button" title="Remove this file"
                  className="text-gray-400 hover:text-red-600 shrink-0"
                  disabled={busy} onClick={() => remove(a)}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Shown, not hidden. A file that was here and is not any more is
          part of the trail, and concealing it defeats the point of
          keeping the record. */}
      {removed.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
          {removed.map((a) => (
            <p key={a.id} className="text-[11px] text-gray-400">
              <s>{a.fileName}</s> — removed
              {a.supersededByName ? ` by ${a.supersededByName}` : ''}
              {a.supersededReason ? `: ${a.supersededReason}` : ''}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
